import { useEffect, useRef } from "react";
import { useAuth } from "../lib/auth";
import { config } from "../lib/config";

// Google's official single-color "G" mark, rendered via currentColor so it
// inherits the themed pill's text color (matches GIS's own `filled_black`
// theme, where the G is white against the dark chip). Per Google's branding
// guidelines the mark must not be recolored (outside of the sanctioned
// single-color variants), cropped, or distorted.
function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="currentColor"
        d="M23.52 12.273c0-.852-.076-1.67-.218-2.455H12v4.64h6.458c-.278 1.5-1.122 2.77-2.391 3.622v3.01h3.872c2.264-2.085 3.581-5.154 3.581-8.817zM12 24c3.24 0 5.957-1.074 7.94-2.91l-3.873-3.01c-1.073.72-2.447 1.147-4.067 1.147-3.125 0-5.77-2.11-6.715-4.946H1.29v3.108A11.997 11.997 0 0 0 12 24zM5.285 14.281a7.212 7.212 0 0 1 0-4.562V6.611H1.29a12.003 12.003 0 0 0 0 10.778l3.995-3.108zM12 4.773c1.762 0 3.344.607 4.59 1.796l3.433-3.434C17.952 1.19 15.235 0 12 0A11.997 11.997 0 0 0 1.29 6.611l3.995 3.108C6.23 6.883 8.875 4.773 12 4.773z"
      />
    </svg>
  );
}

export function SignInButton() {
  const { user, ready, signOut, renderGoogleButton } = useAuth();
  const overlayRef = useRef<HTMLDivElement>(null);

  // Mount the real GIS button into the invisible overlay on top of our themed
  // pill. Using `renderButton` (rather than `prompt()`) keeps us off the
  // `/gsi/log` telemetry path that adblockers blocklist with
  // `ERR_BLOCKED_BY_CLIENT`, while the visual chrome we care about comes from
  // the themed pill painted underneath.
  useEffect(() => {
    if (!ready || user) return;
    const host = overlayRef.current;
    if (!host) return;
    renderGoogleButton(host);
  }, [ready, user, renderGoogleButton]);

  if (!config.googleClientId) {
    return (
      <span className="auth-placeholder" title="VITE_GOOGLE_CLIENT_ID not set">
        sign-in disabled
      </span>
    );
  }

  if (user) {
    const initial =
      user.email?.trim()?.charAt(0)?.toUpperCase() ??
      user.name?.trim()?.charAt(0)?.toUpperCase() ??
      "?";
    return (
      <div className="auth-user">
        {user.picture ? (
          <img
            src={user.picture}
            alt=""
            width={28}
            height={28}
            referrerPolicy="no-referrer"
          />
        ) : (
          <span className="auth-avatar-fallback" aria-hidden>
            {initial}
          </span>
        )}
        <span className="auth-user-email">{user.email}</span>
        <button type="button" className="btn-nav-signout" onClick={signOut}>
          Sign out
        </button>
      </div>
    );
  }

  return (
    <div className="auth-signin">
      {/* Themed pill rendered underneath for visual chrome only. Marked
          aria-hidden because the real interactive control is the GIS button
          iframe overlaid on top — it carries its own accessible name. */}
      <div className="btn-google" aria-hidden="true">
        <GoogleG />
        <span>{ready ? "Sign in with Google" : "Loading…"}</span>
      </div>
      <div
        ref={overlayRef}
        className="gsi-overlay"
        aria-label="Sign in with Google"
      />
    </div>
  );
}
